/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import { useState, useCallback, useEffect, createRef, useMemo } from 'react';
import detectIt from 'detect-it';
import { hasActiveSubscriptionToPlatformBatch } from '@models/Me';
import {
  getBatchGroupSubscriptions,
  getIsLoggedIn,
  getJWTToken
} from '@selectors/auth';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { PLATFORM_BATCH_PAGE, PLATFORM_BATCH_VIEWED } from '@constants/segment';
import { Spacings, mediaQuery } from '@styles/index';
import { scrollConstants } from '@utils/constants';
import useScrollHandlerGroup from '@hooks/useScrollHandlerGroup';
import TTU from '@cont/TalkToUnacademy/PlatformTTU';
import PlatformBatchHeader from '@cont/BatchGroup/PlatFormBatchHeader';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { segment } from '@actions/analytics';
import usePlatformEventProps from '@hooks/usePlatformBatchEventProps';
import { fetchGroupBatchData } from '@actions/batchGroups';
import { useNotifyMeStore } from '@stores/batchGroup/notifyMeStore';
import {
  getIsBatchAccessEnded,
  getEntitySubType,
  getTagsConfig,
  getTabRefs,
  getShowFeedback,
  getActiveStatus
} from '@cont/BatchGroup/utils';
import { useSnackbar } from '@stores/snackbar';
import SubscriptionFeedbackCard from '@cont/BatchGroup/SubscriptionFeedbackCard';
import {
  eventLPSS,
  ACTIVITY_PAGE_SCROLLED,
  PLATFORM_PAGE_LAND,
  BATCH_TAB_CLICKED
} from '@cont/BatchGroup/constants/events';
import PFTabs from '@cont/BatchGroup/PFTabs';
import PFBatchFaqs from '@cont/BatchGroup/PFBatchFaqs';
import EducatorList from '@cont/BatchGroup/EducatorList';
import usePlatformGroupAnalytics from '@cont/BatchGroup/usePlatformGroupAnalytics';
import BatchStateCard from '@cont/BatchGroup/StickyCard/BatchStateCard';
import NotifyMeState from '@cont/BatchGroup/NotifyMe/NotifyMeState';
import notificationsType, {
  BatchType,
  BG_OVERVIEW_TAB_ORDERING,
  Sections,
  STATIC_BLOCK_SECTIONS
} from '@cont/BatchGroup/constants';
import OverViewSection from '@cont/BatchGroup/WebKeyOutcomes/OverViewSection';
import Curriculum from '@cont/BatchGroup/Curriculum';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { abProxyJWT } from '@utils/abHelper';
import { localStorageAvailable } from '@utils/localStorage';

const PlatformTTU = styled(TTU)`
  margin: ${Spacings.SPACING_0B} auto ${Spacings.SPACING_27B} auto;
`;

const SectionItemsGroup = styled.div`
  margin-bottom: ${Spacings.SPACING_20B};
  grid-column: span 16;
  margin-top: ${({ $isFirstTab }) => {
    return $isFirstTab ? Spacings.SPACING_12B : Spacings.SPACING_0B;
  }};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_14B};
    margin-top: ${({ $isFirstTab }) => {
      return $isFirstTab ? Spacings.SPACING_14B : Spacings.SPACING_0B;
    }};
  }
`;

const Container = styled.div`
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_18B};
  }
`;
const ContentWrapper = styled.div``;
const InnerWrapper = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-template-columns: auto;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;
const Wrapper = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;
const HeaderWrapper = styled.div`
  background: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  z-index: 3;
  min-height: ${Spacings.SPACING_17B};
`;

const Background = styled.div`
  position: sticky;
  top: ${Spacings.SPACING_0B};
  height: ${Spacings.SPACING_17B};
  background: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  z-index: 4;
  ${mediaQuery.uptoMobile} {
    height: ${Spacings.SPACING_15B};
  }
`;

// it will stored fired events so it prevent multiple event triggering for same section
const firedScrollEventsMapping = {};

const Batch = ({
  batchDetails,
  trialClasses,
  faqs,
  liveBatchCourses,
  recordedBatchCourses
}) => {
  const batchGroupSubscriptions = useSelector(getBatchGroupSubscriptions);
  const isSubscribedToBatchGroup = hasActiveSubscriptionToPlatformBatch(
    batchGroupSubscriptions,
    batchDetails.batchGroup
  );
  const isLoggedIn = useSelector(getIsLoggedIn);
  const meJWT = useSelector(getJWTToken);
  const { setIsNotified } = useNotifyMeStore();

  const dispatch = useDispatch();
  const { open: showSnackbar } = useSnackbar();

  const batchUID = batchDetails?.batchUID;
  const authors = useMemo(
    () => batchDetails?.authors || [],
    [batchDetails?.authors]
  );

  const isShowFeedback = useMemo(() => {
    return getShowFeedback(batchDetails);
  }, [batchDetails]);

  const [scroll, setScroll] = useState(false);
  const [tabData, setTabData] = useState([]);
  const [selectedTab, setSelectedTab] = useState(null);
  const [firstTab, setFirstTab] = useState(null);
  const [ttuEventsProps, setTtuEventsProps] = useState({});
  const [totalGridRows, setTotalGridRows] = useState(2);
  const [isLiveSection, setIsLiveSection] = useState(false);
  const [isRecordedSection, setIsRecordedSection] = useState(false);
  const isEnrolled = batchDetails?.isEnrolled ?? false;
  const tabsConfig = useMemo(() => getTagsConfig(isEnrolled), [isEnrolled]);
  const synchBatchDataAndNotification = async () => {
    const response = await dispatch(
      fetchGroupBatchData(batchDetails.batchGroup)
    );
    const notifications = response?.rawResponse?.is_notified ?? {
      [notificationsType.UPCOMING_BATCHES]: false,
      [notificationsType.ONGOING_BATCHES]: false
    };
    setIsNotified(notifications);
  };

  useEffect(() => {
    fetchABExperimentData(
      BG_OVERVIEW_TAB_ORDERING,
      isLoggedIn ? meJWT : abProxyJWT
    );
    if (isLoggedIn) {
      synchBatchDataAndNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    const toBeRemoved = [];

    if (!recordedBatchCourses.schedule?.length) {
      toBeRemoved.push(Sections.LESSONS);
      setIsRecordedSection(false);
    } else {
      setIsRecordedSection(true);
    }

    if (!liveBatchCourses.schedule?.length) {
      toBeRemoved.push(Sections.BATCH_SCHEDULE);
      setIsLiveSection(false);
    } else {
      setIsLiveSection(true);
    }

    if (!authors?.length) {
      toBeRemoved.push(Sections.EDUCATORS);
    }
    const filteredTabData = Object.values(tabsConfig).filter(
      (d) => !toBeRemoved.includes(d.key)
    );
    setTabData(filteredTabData);
    setFirstTab(filteredTabData[0]?.key);

    if (!selectedTab) {
      setSelectedTab(filteredTabData[0]?.value ?? 0);
    }
    setTotalGridRows(filteredTabData?.length + STATIC_BLOCK_SECTIONS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    liveBatchCourses.schedule,
    recordedBatchCourses.schedule,
    trialClasses,
    authors,
    isSubscribedToBatchGroup
  ]);

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop } = document.documentElement;
      if (scrollTop > 300 && !scroll) setScroll(true);
      else if (scrollTop < 300 && scroll) setScroll(false);
    };
    window.addEventListener(
      'scroll',
      handleScroll,
      detectIt.passiveEvents === true
        ? { capture: false, passive: true }
        : false
    );
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scroll]);
  const goalUID = batchDetails?.goal?.uid;
  const sendAnalyticsToSegment = usePlatformGroupAnalytics(
    goalUID,
    batchDetails
  );

  const entitySubType = useMemo(
    () => getEntitySubType(batchDetails?.classType),
    [batchDetails?.classType]
  );

  useEffect(() => {
    sendAnalyticsToSegment(PLATFORM_PAGE_LAND, {
      last_primary_source_section: PLATFORM_BATCH_PAGE
    });

    setTtuEventsProps({
      entity_title: batchDetails.name,
      entity_content_type: 'Platform Batch Group',
      last_primary_source_section: `${PLATFORM_BATCH_PAGE} - Bottom TTU`,
      entity_sub_type: entitySubType
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const segmentData = usePlatformEventProps({
    batch: batchDetails,
    entityContentType: true,
    lastPrimarySourceSection: PLATFORM_BATCH_PAGE,
    isBatch: true,
    isSlashedPriceType: true,
    extraProps: { price: batchDetails.price, entity_sub_type: entitySubType }
  });

  useEffect(() => {
    segment.track(PLATFORM_BATCH_VIEWED, segmentData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleRef = createRef();
  const educatorRef = createRef();
  const faqsRef = createRef();
  const aboutRef = createRef();
  const lessonRef = createRef();

  const refs = useMemo(() => {
    return getTabRefs(isEnrolled, {
      scheduleRef,
      lessonRef,
      aboutRef,
      educatorRef,
      faqsRef
    });
  }, [aboutRef, educatorRef, faqsRef, isEnrolled, lessonRef, scheduleRef]);

  // references must be sent in the order they appear on UI

  const offsetTop = scrollConstants.scroll32;
  const { activeStatuses, scrollToElement } = useScrollHandlerGroup(refs, {
    offsetTop
  });

  const {
    isAboutSectionActive,
    isScheduleSectionActive,
    isLessonSectionActive,
    isEducatorSectionActive,
    isFAQsSectionActive
  } = useMemo(() => {
    return getActiveStatus(isEnrolled, activeStatuses);
  }, [activeStatuses, isEnrolled]);

  const scrollToRef = (ref, id) => {
    scrollToElement(ref.current, id);
  };

  const handleChange = (tab) => {
    segmentData.tab = tab.label;
    segment.track(BATCH_TAB_CLICKED, segmentData);
    setSelectedTab(tab.id);
    if (tab.key === Sections.LESSONS) scrollToRef(lessonRef, Sections.LESSONS);
    else if (tab.key === Sections.BATCH_SCHEDULE) {
      scrollToRef(scheduleRef, Sections.BATCH_SCHEDULE);
    } else if (tab.key === Sections.ABOUT) {
      scrollToRef(aboutRef, Sections.ABOUT);
    } else if (tab.key === Sections.EDUCATORS)
      scrollToRef(educatorRef, Sections.EDUCATORS);
    else if (tab.key === Sections.FAQS) {
      scrollToRef(faqsRef, Sections.FAQS);
    }
  };
  const getLpss = (selectedTabIndex) => {
    if (selectedTabIndex === tabsConfig[Sections.ABOUT].value) {
      firedScrollEventsMapping[tabsConfig[Sections.ABOUT].value] = true;
      return eventLPSS.BATCH_AND_OVERVIEW;
    }
    if (selectedTabIndex === tabsConfig[Sections.BATCH_SCHEDULE].value) {
      firedScrollEventsMapping[
        tabsConfig[Sections.BATCH_SCHEDULE].value
      ] = true;
      return eventLPSS.SCHEDULE;
    }
    if (selectedTabIndex === tabsConfig[Sections.LESSONS].value) {
      firedScrollEventsMapping[tabsConfig[Sections.LESSONS].value] = true;
      return eventLPSS.LESSONS;
    }
    if (selectedTabIndex === tabsConfig[Sections.EDUCATORS].value) {
      firedScrollEventsMapping[tabsConfig[Sections.EDUCATORS].value] = true;

      return eventLPSS.EDUCATORS;
    }
    if (selectedTabIndex === tabsConfig[Sections.FAQS].value) {
      firedScrollEventsMapping[tabsConfig[Sections.FAQS].value] = true;
      return eventLPSS.FAQ_AND_TTUN_CTA;
    }
  };

  const sendEventsOnPageScroll = (selectedTabIndex) => {
    // if event is already fired on a section it will not fire for the same section.
    if (firedScrollEventsMapping[selectedTabIndex]) return null;
    const lpss = getLpss(selectedTabIndex);
    segment.track(ACTIVITY_PAGE_SCROLLED, {
      last_primary_source_section: lpss
    });
  };

  const getSelectedTab = useCallback(() => {
    let newSelectedTab = selectedTab;
    if (isLessonSectionActive) {
      newSelectedTab = tabsConfig[Sections.LESSONS].value;
    } else if (isScheduleSectionActive) {
      newSelectedTab = tabsConfig[Sections.BATCH_SCHEDULE].value;
    } else if (isAboutSectionActive) {
      newSelectedTab = tabsConfig[Sections.ABOUT].value;
    } else if (isEducatorSectionActive) {
      newSelectedTab = tabsConfig[Sections.EDUCATORS].value;
    } else if (isFAQsSectionActive) {
      newSelectedTab = tabsConfig[Sections.FAQS].value;
    }
    if (newSelectedTab === selectedTab) return null;
    setSelectedTab(newSelectedTab);
    sendEventsOnPageScroll(newSelectedTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLessonSectionActive,
    isScheduleSectionActive,
    isEducatorSectionActive,
    isFAQsSectionActive,
    isAboutSectionActive
  ]);

  useEffect(() => {
    getSelectedTab();
  }, [getSelectedTab]);

  const isBatchAccessEnded = getIsBatchAccessEnded(
    batchDetails?.batchExpiryDate
  );
  const notifyUserOnce = () => {
    const isLocalStorageAvailable = localStorageAvailable();
    if (isLocalStorageAvailable) {
      const { batchGroup } = batchDetails;
      let notifiedToBatches = localStorage.getItem('notifiedToBatches');
      if (notifiedToBatches === null)
        notifiedToBatches = { [batchGroup]: false };
      else {
        notifiedToBatches = JSON.parse(notifiedToBatches);
      }
      const isNotifiedForBatch = notifiedToBatches[batchGroup];
      if (isNotifiedForBatch) return null;

      if (isEnrolled && isLoggedIn) {
        showSnackbar({
          message: "You've already enrolled to receive updates",
          type: 'success'
        });
        const updatedNotifiedToBatches = {
          ...notifiedToBatches,
          [batchGroup]: true
        };
        localStorage.setItem(
          'notifiedToBatches',
          JSON.stringify(updatedNotifiedToBatches)
        );
      }
    }
  };

  useEffect(() => {
    notifyUserOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnrolled, isLoggedIn]);

  useEffect(() => {
    if (isEnrolled && isBatchAccessEnded) {
      return showSnackbar({
        message: 'Batch access ended',
        type: 'error'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBatchAccessEnded, isEnrolled]);

  return (
    <Container id="BatchGroupDetails">
      <HeaderWrapper>
        <Wrapper>
          <PlatformBatchHeader
            batchGroupUID={batchDetails.uid}
            scroll={scroll}
            lastPrimarySourceSection={`${PLATFORM_BATCH_PAGE} - Page Header`}
            entitySubType={entitySubType}
          />
          <NotifyMeState batchDetails={batchDetails} />
        </Wrapper>
      </HeaderWrapper>
      <ContentWrapper>
        <Background />
        <InnerWrapper>
          <BatchStateCard
            batchDetails={batchDetails}
            totalGridRows={totalGridRows}
            entitySubType={entitySubType}
          />
          <PFTabs
            tabData={tabData}
            selectedTab={selectedTab}
            handleChange={handleChange}
          />
          {isShowFeedback && (
            <SubscriptionFeedbackCard
              goalUID={goalUID}
              feedbackData={batchDetails?.feedbackData}
              isShowFeedback={isShowFeedback}
            />
          )}
          {!isEnrolled && (
            <SectionItemsGroup
              ref={aboutRef}
              $isFirstTab={Sections.ABOUT === firstTab}
            >
              <OverViewSection
                trialClasses={trialClasses}
                batchUID={batchUID}
                goalUID={goalUID}
                disabledTopicGroup
                hideLessonCount
                isEnrolled={false}
                batchDetails={batchDetails}
              />
            </SectionItemsGroup>
          )}
          {isLiveSection && (
            <SectionItemsGroup
              ref={scheduleRef}
              $isFirstTab={Sections.BATCH_SCHEDULE === firstTab}
            >
              <Curriculum
                title="Schedule"
                subTitle="Full schedule"
                batch={batchDetails}
                goalUID={goalUID}
                isBatchGroup
                batchGroupCourses={liveBatchCourses}
                forPlus
                isBatch
                lastPrimarySourceSection="Schedule"
                isSubscribed={isSubscribedToBatchGroup}
                classType={BatchType.liveBatch}
                segmentData={segmentData}
              />
            </SectionItemsGroup>
          )}
          {isRecordedSection && (
            <SectionItemsGroup
              ref={lessonRef}
              $isFirstTab={Sections.LESSONS === firstTab}
            >
              <Curriculum
                title="Lessons"
                subTitle="All lessons"
                batch={batchDetails}
                goalUID={goalUID}
                batchGroupCourses={recordedBatchCourses}
                lastPrimarySourceSection="Lessons"
                isSubscribed={isSubscribedToBatchGroup}
                classType={BatchType.recordedBatch}
                segmentData={segmentData}
              />
            </SectionItemsGroup>
          )}
          {(authors?.length && (
            <SectionItemsGroup
              ref={educatorRef}
              $isFirstTab={Sections.EDUCATORS === firstTab}
            >
              <EducatorList authors={authors} goalUID={goalUID} />
            </SectionItemsGroup>
          )) ||
            null}
          {isEnrolled && (
            <SectionItemsGroup
              $isFirstTab={Sections.ABOUT === firstTab}
              ref={aboutRef}
            >
              <OverViewSection isEnrolled batchDetails={batchDetails} />
            </SectionItemsGroup>
          )}
          {(faqs?.length && (
            <SectionItemsGroup
              ref={faqsRef}
              $isFirstTab={Sections.FAQS === firstTab}
            >
              <PFBatchFaqs faqs={faqs} />
            </SectionItemsGroup>
          )) ||
            null}
          <PlatformTTU ttuEventsProps={ttuEventsProps} />
        </InnerWrapper>
      </ContentWrapper>
    </Container>
  );
};

export default Batch;

Batch.propTypes = {
  batchDetails: PropTypes.objectOf(PropTypes.any),
  schedule: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  trialClasses: PropTypes.objectOf(PropTypes.objectOf(PropTypes.any)),
  faqs: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any))
};

Batch.defaultProps = {
  batchDetails: null,
  trialClasses: {},
  faqs: []
};
